import React, { useState, useEffect } from 'react';
import StepIndicator from '../components/wizard/StepIndicator';
import WizardNavigation from '../components/wizard/WizardNavigation';
import { api } from '../utils/api';

const ProvisionDevicePage = ({ provisionToken: propToken, onComplete, isOverlay = false }) => {
        const [provision, setProvision] = useState(propToken || '');
        const [currentStep, setCurrentStep] = useState(0);
        const [deviceSetupClicked, setDeviceSetupClicked] = useState(false);
        const [connectionStatus, setConnectionStatus] = useState('idle'); // 'idle' | 'checking' | 'connected' | 'uncertain'
        const [waitingForDevice, setWaitingForDevice] = useState(false);
        const [waitingCountdown, setWaitingCountdown] = useState(60);
        const [deviceFound, setDeviceFound] = useState(false);
        const [deviceFailed, setDeviceFailed] = useState(false);
        const [blinkOn, setBlinkOn] = useState(false);

        // Fetch provision parameter from URL (only if not provided as prop)
        useEffect(() => {
                if (!propToken) {
                        const params = new URLSearchParams(window.location.search);
                        const provisionParam = params.get('provision');
                        if (provisionParam) {
                                setProvision(provisionParam);
                        }
                }
        }, [propToken]);

        // Check if connected to device SoftAP (no internet) when on step 3
        useEffect(() => {
                if (currentStep !== 3) {
                        setConnectionStatus('idle');
                        return;
                }

                setConnectionStatus('checking');
                let disconnectedCount = 0;
                let checkCount = 0;
                const maxChecks = 5; // 5 checks over 5 seconds
                let interval;

                const checkConnectivity = async () => {
                        try {
                                const controller = new AbortController();
                                const timeoutId = setTimeout(() => controller.abort(), 800);

                                await fetch('/api/v2/health', { signal: controller.signal });
                                clearTimeout(timeoutId);
                                // If we get here, internet is working - NOT connected to device
                        } catch (error) {
                                // Request failed or timed out - likely connected to device SoftAP
                                disconnectedCount++;
                        }
                        checkCount++;

                        if (checkCount >= maxChecks) {
                                clearInterval(interval);
                                // After 5 seconds, determine final status
                                if (disconnectedCount >= 3) {
                                        // Majority of checks failed = connected to device
                                        setConnectionStatus('connected');
                                } else {
                                        // Internet still works = uncertain (might be ethernet)
                                        setConnectionStatus('uncertain');
                                }
                        }
                };

                interval = setInterval(checkConnectivity, 1000);
                checkConnectivity(); // Check immediately

                return () => clearInterval(interval);
        }, [currentStep]);

        // Blinking effect for step images
        useEffect(() => {
                if (currentStep === 1 || currentStep === 2) {
                        const blinkInterval = setInterval(() => {
                                setBlinkOn(prev => !prev);
                        }, 500);
                        return () => clearInterval(blinkInterval);
                }
                setBlinkOn(false);
        }, [currentStep]);

        // Listen for postMessage from device setup window
        useEffect(() => {
                const handleMessage = (event) => {
                        // Accept message from device setup (192.168.4.1)
                        if (event.data?.type === 'DEVICE_SETUP_COMPLETE') {
                                console.log("Device Setup Complete");
                                setCurrentStep(4);
                                setWaitingForDevice(true);
                        }
                };

                window.addEventListener('message', handleMessage);
                return () => window.removeEventListener('message', handleMessage);
        }, []);

        // Polling and countdown for waiting for device on step 4
        useEffect(() => {
                if (currentStep !== 4 || !waitingForDevice || deviceFound) {
                        return;
                }

                // Countdown timer
                const countdownInterval = setInterval(() => {
                        setWaitingCountdown(prev => {
                                if (prev <= 1) {
                                        clearInterval(countdownInterval);
                                        setDeviceFailed(true);
                                        return 0;
                                }
                                return prev - 1;
                        });
                }, 1000);

                // Poll for device every 5 seconds
                const pollInterval = setInterval(async () => {
                        try {
                                const response = await api(`/devices/provision/${provision}`, {
                                        method: 'GET',
                                });

                                if (response.ok) {
                                        const data = await response.json();
                                        if (data.data?.device_id) {
                                                setDeviceFound(true);
                                                clearInterval(pollInterval);
                                                clearInterval(countdownInterval);
                                        }
                                }
                        } catch (error) {
                                // Ignore errors, keep polling
                        }
                }, 5000);

                return () => {
                        clearInterval(countdownInterval);
                        clearInterval(pollInterval);
                };
        }, [currentStep, waitingForDevice, deviceFound, provision]);

        const totalSteps = 5;

        const canGoNext = () => {
                if (currentStep === 3) {
                        return deviceSetupClicked;
                }
                if (currentStep === 4) {
                        return deviceFound || deviceFailed;
                }
                return true;
        };

        const handleNext = () => {
                if (currentStep === 2) {
                        setDeviceSetupClicked(true);
                        window.open(`http://192.168.4.1?token=${provision}&callback=${window.location.origin}`, '_blank');
                }
                if (currentStep === 3) {
                        setWaitingForDevice(true);
                        setWaitingCountdown(60);
                }
                if (currentStep < totalSteps - 1) {
                        setCurrentStep(currentStep + 1);
                }
        };

        const handleBack = () => {
                if (currentStep === 1) {
                        setCurrentStep(0);
                } else if (currentStep > 0) {
                        setCurrentStep(currentStep - 1);
                }
        };

        const handleFinish = () => {
                if (deviceFound) {
                        // Success - open device dashboard
                        onComplete({ success: true, provision });
                } else if (deviceFailed) {
                        // Failure - open troubleshoot
                        onComplete({ success: false, provision });
                }
        };

        const renderStepContent = () => {
                // Step 0: Preparation
                if (currentStep === 0) {
                        return (
                                <div className="py-6 flex flex-col md:flex-row md:items-start gap-6">
                                        <div className="flex-1">
                                                <h3 data-testid="provision-step" data-step="prep" className="text-xl font-semibold text-gray-900 mb-6">Preparation</h3>
                                                <div className="space-y-4">
                                                        <div className="flex items-start">
                                                                <div className="bg-brand/10 text-brand font-bold rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0">
                                                                        ✓
                                                                </div>
                                                                <div className="flex-1">
                                                                        <p className="text-gray-700">
                                                                                Have your CleverPlant device in front of you.
                                                                        </p>
                                                                </div>
                                                        </div>
                                                        <div className="flex items-start">
                                                                <div className="bg-brand/10 text-brand font-bold rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0">
                                                                        ✓
                                                                </div>
                                                                <div className="flex-1">
                                                                        <p className="text-gray-700">
                                                                                Be sure you know the name of your Internet Wi-Fi network and password.
                                                                        </p>
                                                                </div>
                                                        </div>
                                                        <div className="flex items-start">
                                                                <div className="bg-brand/10 text-brand font-bold rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0">
                                                                        ✓
                                                                </div>
                                                                <div className="flex-1">
                                                                        <p className="text-gray-700">
                                                                                This computer needs Wi-Fi to connect to the device during setup.
                                                                        </p>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                        <div className="flex-shrink-0">
                                                <img
                                                        src="/images/pairing/step0.png"
                                                        alt="CleverPlant device"
                                                        className="max-w-[100px] h-auto"
                                                />
                                        </div>
                                </div>
                        );
                }

                if (currentStep === 1) {
                        // Step 1: Prepare Device
                        return (
                                <div className="py-6 flex flex-col md:flex-row md:items-start gap-6">
                                        <div className="flex-1">
                                                <h3 data-testid="provision-step" data-step="pairing" className="text-xl font-semibold text-gray-900 mb-6">Set your CleverPlant device to Pairing Mode</h3>
                                                <div className="space-y-4">
                                                        <div className="flex items-start">
                                                                <div className="bg-brand/10 text-brand font-bold rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0">
                                                                        2
                                                                </div>
                                                                <div className="flex-1">
                                                                        <p className="text-gray-700 mb-2">
                                                                                Press and hold the button on the device.<br />When it starts blinking <span className="text-blue-600 font-medium">blue</span>, it is ready.
                                                                        </p>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                        <div className="flex-shrink-0">
                                                <img
                                                        src={`/images/pairing/step2${blinkOn ? '_on' : ''}.png`}
                                                        alt="Device in pairing mode"
                                                        className="max-w-[200px] h-auto"
                                                />
                                        </div>
                                </div>
                        );
                }

                if (currentStep === 2) {
                        // Step 2: Connect to Wi-Fi
                        return (
                                <div className="py-6">
                                        <h3 data-testid="provision-step" data-step="wifi" className="text-xl font-semibold text-gray-900 mb-6">Connect to CleverPlant Wi-Fi on your PC</h3>
                                        <div className="flex flex-col md:flex-row md:items-stretch gap-6">
                                                <div className="flex-1 flex flex-col justify-between">
                                                        <div>
                                                                <div className="space-y-4">
                                                                        <div className="flex items-start">
                                                                                <div className="bg-brand/10 text-brand font-bold rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0">
                                                                                        3
                                                                                </div>
                                                                                <div className="flex-1">
                                                                                        <p className="text-gray-700 mb-2">
                                                                                                Connect to the Wi-Fi named<br />{' '}
                                                                                                <span className="font-bold text-brand">CleverPlant-Setup</span>
                                                                                                <br /><br />
                                                                                                When the device starts blinking <span className="text-green-600 font-medium">green</span>, it is ready.
                                                                                        </p>
                                                                                </div>
                                                                        </div>
                                                                </div>
                                                        </div>
                                                        <div className="mt-6">
                                                                <p className="text-sm text-gray-600">
                                                                        Need help connecting to Wi-Fi on Windows?
                                                                </p>
                                                                <p className="text-sm text-gray-500">
                                                                        <a
                                                                                href="https://support.microsoft.com/en-us/windows/connect-to-a-wi-fi-network-in-windows-1f881677-b569-0cd5-010d-e3cd3579d263"
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="text-brand hover:underline"
                                                                        >
                                                                                How to connect to Wi-Fi in Windows
                                                                        </a>
                                                                </p>
                                                        </div>
                                                </div>
                                                <div className="flex-shrink-0">
                                                        <img
                                                                src={`/images/pairing/laptop${blinkOn ? '1' : '0'}.png`}
                                                                alt="Connect to Wi-Fi"
                                                                className="max-w-[300px] h-auto"
                                                        />
                                                </div>
                                        </div>
                                </div>
                        );
                }

                if (currentStep === 3) {
                        // Step 3: Waiting for Device Setup callback
                        return (
                                <div className="py-6">
                                        <h3 data-testid="provision-step" data-step="configure" className="text-xl font-semibold text-gray-900 mb-6">Configure Device</h3>
                                        <div className="flex flex-col items-center justify-center py-8">
                                                <svg className="animate-spin h-12 w-12 text-brand mb-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <p className="text-gray-700 text-center">
                                                        Device Setup has opened in a new page.<br />
                                                        Please follow the steps to setup Wi-Fi on device.
                                                </p>
                                        </div>
                                </div>
                        );
                }

                if (currentStep === 4) {
                        // Step 4: Waiting for Device / Success / Unsuccessful

                        // Determine title and icon based on state
                        let title = 'Waiting for Device';
                        let iconClass = 'bg-brand/10 text-brand';
                        let iconContent = '5';

                        if (deviceFound) {
                                title = 'Device Connected!';
                                iconClass = 'bg-green-100 text-green-600';
                                iconContent = '✓';
                        } else if (deviceFailed) {
                                title = 'Connection Failed';
                                iconClass = 'bg-red-100 text-red-600';
                                iconContent = '✗';
                        }

                        return (
                                <div className="py-6 flex flex-col md:flex-row md:items-start gap-6">
                                        <div className="flex-1">
                                                <h3 data-testid="provision-step" data-step="waiting" className="text-xl font-semibold text-gray-900 mb-6">{title}</h3>
                                                <div className="space-y-4">
                                                        <div className="flex items-start">
                                                                <div className={`font-bold rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 ${iconClass}`}>
                                                                        {iconContent}
                                                                </div>
                                                                <div className="flex-1">
                                                                        {deviceFound ? (
                                                                                <>
                                                                                        <p className="text-gray-700 mb-2">
                                                                                                Your CleverPlant device has been successfully configured and is now connected to your dashboard.
                                                                                        </p>
                                                                                        <p className="text-sm text-green-600 font-medium">
                                                                                                Click "Finish" to view your device on the dashboard.
                                                                                        </p>
                                                                                </>
                                                                        ) : deviceFailed ? (
                                                                                <>
                                                                                        <p className="text-gray-700 mb-2">
                                                                                                We couldn't connect to your device. Please try again and make sure:
                                                                                        </p>
                                                                                        <ul className="text-sm text-gray-600 list-disc list-inside mb-4 space-y-1">
                                                                                                <li>Your Wi-Fi password is correct</li>
                                                                                                <li>The device is within range of your Wi-Fi router</li>
                                                                                        </ul>
                                                                                        <p className="text-sm text-red-600 font-medium">
                                                                                                Click "Finish" to troubleshoot.
                                                                                        </p>
                                                                                </>
                                                                        ) : (
                                                                                <>
                                                                                        <p className="text-gray-700 mb-2">
                                                                                                Your device is connecting to your Wi-Fi network...
                                                                                        </p>
                                                                                        <p className="text-sm text-gray-600 mb-4">
                                                                                                The device blinks <span className="text-green-500 font-medium">green</span> while testing the connection, then turns solid <span className="text-green-600 font-medium">green</span> on success or <span className="text-red-600 font-medium">red</span> on failure.
                                                                                        </p>
                                                                                        <div className="flex items-center gap-3">
                                                                                                <svg className="animate-spin h-5 w-5 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                                                </svg>
                                                                                                <span className="text-sm text-gray-600">
                                                                                                        Waiting for device to appear... ({waitingCountdown}s)
                                                                                                </span>
                                                                                        </div>
                                                                                </>
                                                                        )}
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                        <div className="flex-shrink-0">
                                                <img
                                                        src="/images/pairing/step0.png"
                                                        alt="CleverPlant device"
                                                        className="max-w-[100px] h-auto"
                                                />
                                        </div>
                                </div>
                        );
                }
                return null;
        };

        const content = (
                <>
                        <h2 data-testid="provision-title" className="text-2xl font-bold text-gray-900 mb-6">Add New Device</h2>

                        {/* Step Indicator */}
                        <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />

                        {/* Step Content */}
                        <div className="my-8">
                                {renderStepContent()}
                        </div>

                        {/* Wizard Navigation */}
                        {currentStep !== 3 && (
                                <WizardNavigation
                                        currentStep={currentStep}
                                        totalSteps={totalSteps}
                                        onNext={handleNext}
                                        onBack={handleBack}
                                        onFinish={handleFinish}
                                        canGoNext={canGoNext()}
                                        showBack={currentStep !== 4}
                                />
                        )}
                </>
        );
        return content;

};

export default ProvisionDevicePage;
